import {
    ChangeEventHandler,
    MouseEvent,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useNavigate } from 'react-router';
import { OnDragEndResponder } from '@hello-pangea/dnd';

import ModalV2 from '../../shared/ModalV2';
import CopyPacksDrawer from '../CopyPacksDrawer/CopyPacksDrawer';
import { PacksDrawerTable } from '../PacksDrawerTable/PacksDrawerTable';
import { Loading, Modal, Section } from '../../shared';

import { usePacksList } from '../../hooks/packs';
import {
    useDeletePacksDrawer,
    usePacksDrawerList,
    useUpdatePacksDrawerPriorities,
} from '../../hooks/packsDrawer';

import { handleError, moveListItem } from '../../utils/utils';

import { RouteTo } from '../../routes';

import { PacksDrawerRecord } from '../../types/packsDrawerTypes';

import styles from './PacksDrawer.module.scss';

const SEARCH_STORAGE_KEY = 'packsDrawerSearchString';

function filterRecordsBySearchString(
    records: PacksDrawerRecord[],
    searchString: string,
) {
    const normalized = searchString.trim().toLowerCase();

    if (!normalized) {
        return records;
    }

    return records.filter((record) =>
        record.packId.toLowerCase().includes(normalized),
    );
}

export function PacksDrawer() {
    const navigate = useNavigate();
    const {
        records: fetchedRecords,
        errorMessage,
        isLoading,
        fetch,
    } = usePacksDrawerList();
    const { deleteRecord: deleteRecordMutation } = useDeletePacksDrawer();
    const { updatePriorities } = useUpdatePacksDrawerPriorities();
    const { packs } = usePacksList({ enabled: true });
    const [records, setRecords] = useState<PacksDrawerRecord[]>([]);
    const [searchString, setSearchString] = useState(
        localStorage.getItem(SEARCH_STORAGE_KEY) ?? '',
    );
    const [recordIdToCopy, setRecordIdToCopy] = useState<
        PacksDrawerRecord['id'] | undefined
    >();

    useEffect(() => {
        setRecords(fetchedRecords);
    }, [fetchedRecords]);

    const onSearchStringChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const value = e.target.value;
        localStorage.setItem(SEARCH_STORAGE_KEY, value);
        setSearchString(value);
    };

    const redirectToRecordDetails = (id: PacksDrawerRecord['id']) => {
        navigate(RouteTo.PacksDrawerEdit(String(id)));
    };

    const deleteRecord = async (
        e: MouseEvent<HTMLButtonElement>,
        id: PacksDrawerRecord['id'],
    ) => {
        e.stopPropagation();

        const isConfirmed = await Modal.confirm({
            heading: 'Delete packs drawer record',
            text: 'Really delete this packs drawer record?',
            okLabel: 'Yes do it!',
        });

        if (!isConfirmed) return;

        try {
            await deleteRecordMutation(id);
        } catch (error) {
            handleError('Error deleting packs drawer record', error);
        }
    };

    const copyRecord = (
        e: MouseEvent<HTMLButtonElement>,
        id: PacksDrawerRecord['id'],
    ) => {
        e.stopPropagation();
        setRecordIdToCopy(id);
    };

    const onDragEnd: OnDragEndResponder = async (result) => {
        if (!result.destination) {
            return;
        }

        if (result.destination.index === result.source.index) {
            return;
        }

        try {
            const reorderedRecords = moveListItem(
                records,
                result.source.index,
                result.destination.index,
            );
            setRecords(reorderedRecords);

            await updatePriorities(reorderedRecords.map((r) => r.id));
            await fetch();
        } catch (error) {
            handleError("Couldn't reorder packs drawer", error, true);
        }
    };

    const isDragDropDisabled = isLoading || !!searchString.trim();

    const filteredRecords = useMemo(
        () => filterRecordsBySearchString(records, searchString),
        [records, searchString],
    );

    const getPackName = useMemo(() => {
        const nameByPackId = new Map(
            (packs ?? []).map((pack) => [
                pack.packId,
                pack.displayName?.en || pack.name,
            ]),
        );
        return (packId: string) => nameByPackId.get(packId) ?? '';
    }, [packs]);

    if (isLoading && !records.length) {
        return <Loading />;
    }

    if (errorMessage) {
        return <Section>{errorMessage}</Section>;
    }

    return (
        <Section>
            {recordIdToCopy !== undefined && (
                <ModalV2>
                    <CopyPacksDrawer
                        recordId={recordIdToCopy}
                        onClose={() => setRecordIdToCopy(undefined)}
                    />
                </ModalV2>
            )}
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <h1>Packs Drawer ({filteredRecords.length})</h1>
                    <button
                        className="new"
                        onClick={() => navigate(RouteTo.PacksDrawerNew())}
                    >
                        Add new
                    </button>
                </div>
                <input
                    aria-label="search packs drawer"
                    autoFocus
                    className="editInput"
                    type="text"
                    value={searchString}
                    placeholder="Search by pack id..."
                    onChange={onSearchStringChange}
                />
            </div>
            {filteredRecords.length ? (
                <PacksDrawerTable
                    records={filteredRecords}
                    getPackName={getPackName}
                    onRecordClick={redirectToRecordDetails}
                    onDeleteRecordClick={deleteRecord}
                    onCopyRecordClick={copyRecord}
                    onDragEnd={onDragEnd}
                    isDragDropDisabled={isDragDropDisabled}
                />
            ) : (
                <div>No packs drawer records</div>
            )}
        </Section>
    );
}
